import { alpha, createTheme } from '@mui/material/styles';

export type AppThemeMode = 'light' | 'dark';

// Custom theme with optimized spacing, typography, and component overrides
// for better data density and modern appearance
export const createAppTheme = (mode: AppThemeMode) =>
  createTheme({
    // Custom spacing scale: 5px base (vs 8px default) = 40% reduction
    spacing: 5,

    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background:
        mode === 'dark'
          ? {
              default: '#121212',
              paper: '#1e1e1e',
            }
          : {
              default: '#f5f5f5',
              paper: '#ffffff',
            },
      ...(mode === 'dark'
        ? {
            action: {
              hover: alpha('#fff', 0.06),
            },
          }
        : {}),
    },

    // Optimized typography for better data density
    typography: {
      fontSize: 13,
      htmlFontSize: 16,
      h1: {
        fontSize: '2rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.1rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.8125rem',
      },
      button: {
        textTransform: 'none',
        fontSize: '0.875rem',
      },
    },

    components: {
      // Global styles: Prevent horizontal overflow
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            overflowX: 'hidden',
            overflowY: 'hidden',
            width: '100%',
          },
          body: {
            overflowX: 'hidden',
            overflowY: 'hidden',
            width: '100%',
            ...(mode === 'dark'
              ? {
                  scrollbarColor: 'rgba(255,255,255,0.12) #1e1e1e',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: '10px',
                    height: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#1e1e1e',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    borderRadius: 8,
                  },
                }
              : {}),
          },
          '#root': {
            overflowX: 'hidden',
            overflowY: 'hidden',
            width: '100%',
            ...(mode === 'dark'
              ? {
                  '&::-webkit-scrollbar': {
                    width: '10px',
                    height: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#1e1e1e',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    borderRadius: 8,
                  },
                }
              : {}),
          },
        },
      },

      MuiModal: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiDialog: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },

      // Container: Fluid width by default, reduced padding
      MuiContainer: {
        defaultProps: {
          maxWidth: false,
        },
        styleOverrides: {
          root: {
            paddingLeft: '10px',
            paddingRight: '10px',
          },
        },
      },

      // TableCell: Reduced padding for better data density
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '10px',
          },
          head: {
            fontWeight: 600,
            padding: '10px',
          },
          body: {
            padding: '6px 10px',
          },
        },
      },

      // Paper: Lower elevation, cleaner look
      MuiPaper: {
        defaultProps: {
          elevation: 1,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },

      // Button: Consistent styling, no text transform
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 4,
          },
          sizeSmall: {
            padding: '4px 10px',
            fontSize: '0.8125rem',
          },
          sizeMedium: {
            padding: '6px 16px',
            fontSize: '0.875rem',
          },
          sizeLarge: {
            padding: '8px 22px',
            fontSize: '0.9375rem',
          },
        },
      },

      // Chip: Consistent styling, compact sizes
      MuiChip: {
        styleOverrides: {
          root: {
            height: '26px',
            fontSize: '0.8125rem',
          },
          sizeSmall: {
            height: '22px',
            fontSize: '0.75rem',
          },
        },
      },

      // TextField: Reduced margins and padding
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
      },

      // FormControl: Reduced margins
      MuiFormControl: {
        defaultProps: {
          size: 'small',
          margin: 'dense',
        },
      },

      // InputBase: Optimized input sizing
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
          input: {
            padding: '8px 12px',
          },
        },
      },

      // OutlinedInput: Consistent sizing
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            padding: '8px 12px',
          },
        },
      },

      // Select: Reduced padding
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '8px 12px',
          },
        },
      },

      // Tab: Compact sizing
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: '42px',
            fontSize: '0.875rem',
          },
        },
      },

      // Toolbar: Reduced height
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '56px',
          },
          dense: {
            minHeight: '48px',
          },
        },
      },

      // Dialog: Optimized spacing
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '12px 20px',
            fontSize: '1.1rem',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '16px 20px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 20px',
          },
        },
      },

      // Card: Reduced padding
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px',
            '&:last-child': {
              paddingBottom: '12px',
            },
          },
        },
      },
    },
  });
